import React, { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  Sparkles, Wand2, Loader2, Plus, X, ChevronRight, ChevronLeft,
  ListChecks, Lightbulb, Trash2, Save, FileText, Copy,
} from "lucide-react";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../lib/auth";

const FORMATOS = ["Reels", "Carrossel", "Stories", "Estático", "Vídeo"];

const STEPS = [
  {
    key: "passo_1", title: "1. Público / Persona-Crença",
    desc: "Quem você fala e o que ele já acredita.",
    fields: [
      { key: "crenca", label: "Crença atual do público", type: "textarea", placeholder: "O que ele pensa hoje sobre o tema?" },
      { key: "linguagem", label: "Linguagem que ele usa", type: "text", placeholder: "Palavras/gírias que ele fala" },
      { key: "medo_desejo", label: "Medo ou desejo dominante", type: "textarea" },
    ],
    aiPrompt: (data, tema, big) => `Público brasileiro. Tema: ${tema || "?"}. Big Idea: ${big || "?"}. A partir dos rascunhos: crença="${data.crenca || ""}", linguagem="${data.linguagem || ""}", medo/desejo="${data.medo_desejo || ""}". Refine em 3 bullets: (1) crença mais precisa, (2) 5 expressões típicas dele, (3) medo/desejo dito na voz dele.`,
  },
  {
    key: "passo_2", title: "2. Problema / Desejo",
    desc: "A dor real e o que ele quer no lugar.",
    fields: [
      { key: "problema", label: "Problema/dor concreta", type: "textarea" },
      { key: "desejo", label: "Estado desejado", type: "textarea" },
    ],
    aiPrompt: (data) => `Problema="${data.problema}". Desejo="${data.desejo}". Reescreva em 2 frases curtas de post que ativem o problema e prometam o desejo, no formato "Você [problema]? [Desejo] com [gancho]."`,
  },
  {
    key: "passo_3", title: "3. Big Idea",
    desc: "A tese central. Puxe do banco ou escreva agora.",
    fields: [
      { key: "big_idea", label: "Big Idea (crença antiga → tese nova)", type: "textarea", placeholder: "Ex: Não é sobre postar mais — é sobre postar com posicionamento." },
    ],
    aiPrompt: (data, tema) => `Tema="${tema}". Reescreva a Big Idea "${data.big_idea}" em 3 versões: (a) provocativa, (b) esperançosa, (c) analítica. Máx 20 palavras cada.`,
  },
  {
    key: "passo_4", title: "4. Evidência / Prova",
    desc: "Dados, cases, autoridade, exemplos.",
    fields: [
      { key: "evidencias", label: "Evidências (uma por linha)", type: "list" },
    ],
    aiPrompt: (data, tema, big) => `Big Idea="${big}". Tema="${tema}". Evidências atuais: ${(data.evidencias || []).join("; ")}. Sugira 3 provas concretas adicionais (dado, case ou analogia) que reforçariam essa tese.`,
  },
  {
    key: "passo_5", title: "5. Desconforto / Custo",
    desc: "O que acontece se ele NÃO agir agora.",
    fields: [
      { key: "desconforto", label: "Custo de não mudar", type: "textarea" },
    ],
    aiPrompt: (data) => `Custo atual="${data.desconforto}". Reescreva em 2 versões: (a) racional (números/tempo perdido), (b) emocional (sentimento). Máx 40 palavras cada.`,
  },
  {
    key: "passo_6", title: "6. CTA / Direção",
    desc: "Passo único, específico, agora.",
    fields: [
      { key: "cta", label: "Call to action", type: "text", placeholder: "Ex: Salva este post e volta amanhã." },
      { key: "direcao", label: "Direção seguinte (opcional)", type: "text", placeholder: "Onde ele deve ir depois de agir." },
    ],
    aiPrompt: (data) => `CTA="${data.cta}". Direção="${data.direcao}". Gere 3 CTAs alternativos: (1) baixo atrito ("salva"), (2) médio ("comenta"), (3) alto atrito ("clica no link").`,
  },
];

function ListInput({ value, onChange, placeholder }) {
  const [tmp, setTmp] = useState("");
  const items = Array.isArray(value) ? value : [];
  return (
    <div>
      <div className="flex gap-2">
        <input className="input" value={tmp} placeholder={placeholder} onChange={(e) => setTmp(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && tmp.trim()) { e.preventDefault(); onChange([...items, tmp.trim()]); setTmp(""); } }} />
        <button type="button" onClick={() => { if (tmp.trim()) { onChange([...items, tmp.trim()]); setTmp(""); } }} className="btn-outline"><Plus size={16} /></button>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {items.map((it, i) => (
          <span key={`${it}-${i}`} className="chip flex items-center gap-1">{it}<button onClick={() => onChange(items.filter((_, x) => x !== i))} className="text-muted hover:text-[var(--error)]"><X size={11} /></button></span>
        ))}
      </div>
    </div>
  );
}

function Field({ f, value, onChange }) {
  if (f.type === "textarea") return <textarea className="input" rows={3} placeholder={f.placeholder} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`field-${f.key}`} />;
  if (f.type === "list") return <div data-testid={`field-${f.key}`}><ListInput value={value} onChange={onChange} placeholder={f.placeholder} /></div>;
  return <input className="input" placeholder={f.placeholder} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`field-${f.key}`} />;
}

function ChecklistCard({ passos }) {
  const done = STEPS.filter((s) => {
    const d = passos[s.key] || {};
    return s.fields.some((f) => {
      const v = d[f.key];
      return v && (Array.isArray(v) ? v.length > 0 : true);
    });
  });
  const score = done.length;
  return (
    <div className="card p-4" data-testid="checklist-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ListChecks size={16} className="grad-text" />
          <div className="font-mono text-[10px] uppercase tracking-widest grad-text font-bold">// checklist de persuasão</div>
        </div>
        <div className="font-bold text-lg" data-testid="checklist-score">{score}/6</div>
      </div>
      <div className="progress mb-3"><div style={{ width: `${(score / 6) * 100}%` }} /></div>
      <ul className="space-y-1.5">
        {STEPS.map((s) => {
          const isDone = done.find((d) => d.key === s.key);
          return (
            <li key={s.key} className={`text-xs flex items-center gap-2 ${isDone ? "" : "text-muted"}`}>
              <span className={`w-4 h-4 rounded-full inline-flex items-center justify-center text-[10px] font-bold ${isDone ? "grad-bg text-white" : "card-2"}`}>
                {isDone ? "✓" : ""}
              </span>
              {s.title.replace(/^\d+\.\s*/, "")}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function AssembledCard({ passos, tema, formato }) {
  const p1 = passos.passo_1 || {}, p2 = passos.passo_2 || {}, p3 = passos.passo_3 || {},
        p4 = passos.passo_4 || {}, p5 = passos.passo_5 || {}, p6 = passos.passo_6 || {};
  const inicial = p1.crenca || "—";
  const intervencao = [p3.big_idea, (p4.evidencias || []).slice(0, 2).join(" · "), p5.desconforto].filter(Boolean).join(" — ") || "—";
  const final = [p2.desejo, p6.cta].filter(Boolean).join(" · ") || "—";

  const copyAll = async () => {
    const txt = [
      tema && `Tema: ${tema}`, formato && `Formato: ${formato}`, "",
      "ESTADO INICIAL:", inicial, "",
      "INTERVENÇÃO:", intervencao, "",
      "ESTADO FINAL:", final,
    ].filter((x) => x !== undefined).join("\n");
    try {
      await navigator.clipboard.writeText(txt);
      toast.success("Roteiro copiado");
    } catch {
      toast.error("Não foi possível copiar — permita acesso à área de transferência");
    }
  };

  return (
    <div className="card p-5 relative overflow-hidden" data-testid="assembled-card">
      <div className="absolute inset-0 grad-bg-soft pointer-events-none" />
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest grad-text font-bold">// roteiro montado</div>
            <h3 className="font-bold">Estado inicial → Intervenção → Estado final</h3>
          </div>
          <button onClick={copyAll} className="btn-outline text-xs flex items-center gap-1" data-testid="copy-assembled"><Copy size={12} /> copiar</button>
        </div>
        <div className="space-y-2 text-sm">
          <div className="card-2 p-3"><span className="text-muted text-[10px] font-mono uppercase tracking-widest">estado inicial</span><div className="mt-1">{inicial}</div></div>
          <div className="card-2 p-3"><span className="text-muted text-[10px] font-mono uppercase tracking-widest">intervenção</span><div className="mt-1">{intervencao}</div></div>
          <div className="card-2 p-3"><span className="text-muted text-[10px] font-mono uppercase tracking-widest">estado final</span><div className="mt-1">{final}</div></div>
        </div>
      </div>
    </div>
  );
}

function BigIdeasBank({ items, onAdd, onDelete, onUse }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ crenca_antiga: "", tese_nova: "", provas: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.crenca_antiga || !form.tese_nova) { toast.error("Preencha crença e tese"); return; }
    await onAdd({
      crenca_antiga: form.crenca_antiga,
      tese_nova: form.tese_nova,
      provas: form.provas.split("\n").map((p) => p.trim()).filter(Boolean),
    });
    setForm({ crenca_antiga: "", tese_nova: "", provas: "" });
    setShowForm(false);
  };

  return (
    <div className="card p-5 space-y-3" data-testid="big-ideas-bank">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Lightbulb size={18} className="grad-text" />
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest grad-text font-bold">// banco</div>
            <h3 className="font-bold text-lg">Big Ideas</h3>
          </div>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="btn-grad text-xs flex items-center gap-1" data-testid="new-bigidea"><Plus size={12} /> Nova</button>
      </div>
      <p className="text-xs text-muted">Crença antiga → Tese nova. Clique numa para preencher o passo 3.</p>

      {showForm && (
        <form onSubmit={submit} className="card-2 p-3 space-y-2">
          <textarea className="input" rows={2} placeholder="Crença antiga…" value={form.crenca_antiga} onChange={(e) => setForm({ ...form, crenca_antiga: e.target.value })} data-testid="bi-crenca" />
          <textarea className="input" rows={2} placeholder="Tese nova…" value={form.tese_nova} onChange={(e) => setForm({ ...form, tese_nova: e.target.value })} data-testid="bi-tese" />
          <textarea className="input" rows={2} placeholder="Provas (uma por linha, opcional)" value={form.provas} onChange={(e) => setForm({ ...form, provas: e.target.value })} data-testid="bi-provas" />
          <div className="flex gap-2">
            <button type="submit" className="btn-grad text-xs" data-testid="bi-save">Salvar</button>
            <button type="button" onClick={() => setShowForm(false)} className="btn-outline text-xs">Cancelar</button>
          </div>
        </form>
      )}

      {items.length === 0 ? (
        <div className="text-xs text-muted text-center py-4">Nenhuma big idea salva ainda.</div>
      ) : (
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {items.map((bi) => (
            <div key={bi.id} className="card-2 p-3 group" data-testid={`bi-item-${bi.id}`}>
              <div className="text-[10px] text-muted line-through">{bi.crenca_antiga}</div>
              <div className="text-sm font-semibold mt-1">{bi.tese_nova}</div>
              {bi.provas?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {bi.provas.map((p, i) => <span key={`${bi.id}-p-${i}`} className="chip text-[10px]" style={{ padding: "2px 6px" }}>{p}</span>)}
                </div>
              )}
              <div className="flex gap-2 mt-2">
                <button onClick={() => onUse(bi.tese_nova)} className="text-xs grad-text hover:underline" data-testid={`bi-use-${bi.id}`}>usar no passo 3 →</button>
                <button onClick={() => onDelete(bi.id)} className="text-xs text-muted hover:text-[var(--error)] ml-auto" data-testid={`bi-del-${bi.id}`}><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Content() {
  const { activeBrand } = useAuth();
  const [state, setState] = useState({ big_ideas: [], roteiros: [] });
  const [currentRoteiro, setCurrentRoteiro] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState({});
  const [aiModal, setAiModal] = useState(null); // { title, text }
  const saveDebounce = useRef(null);

  const load = async () => {
    if (!activeBrand) return;
    const { data } = await api.get(`/content/${activeBrand.brand_id}`);
    setState(data);
    if (!currentRoteiro && data.roteiros.length > 0) setCurrentRoteiro(data.roteiros[0]);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeBrand]);

  const createRoteiro = async () => {
    try {
      const { data } = await api.post(`/content/${activeBrand.brand_id}/roteiro`, { title: "Novo roteiro", passos: {} });
      const updated = { ...state, roteiros: [...state.roteiros, data] };
      setState(updated);
      setCurrentRoteiro(data);
      toast.success("Roteiro criado");
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
  };

  const persistRoteiro = (updated) => {
    setCurrentRoteiro(updated);
    setState((s) => ({ ...s, roteiros: s.roteiros.map((r) => r.id === updated.id ? updated : r) }));
    if (saveDebounce.current) clearTimeout(saveDebounce.current);
    saveDebounce.current = setTimeout(async () => {
      setSaving(true);
      try {
        await api.put(`/content/${activeBrand.brand_id}/roteiro/${updated.id}`, {
          title: updated.title,
          tema: updated.tema,
          abordagem: updated.abordagem,
          formato: updated.formato,
          passos: updated.passos || {},
        });
      } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
      finally { setSaving(false); }
    }, 700);
  };

  const setField = (stepKey, fieldKey, val) => {
    const passos = { ...(currentRoteiro.passos || {}) };
    passos[stepKey] = { ...(passos[stepKey] || {}), [fieldKey]: val };
    persistRoteiro({ ...currentRoteiro, passos });
  };

  const setHeader = (patch) => persistRoteiro({ ...currentRoteiro, ...patch });

  const deleteRoteiro = async (id) => {
    if (!window.confirm("Excluir este roteiro?")) return;
    try {
      await api.delete(`/content/${activeBrand.brand_id}/roteiro/${id}`);
      const updated = state.roteiros.filter((r) => r.id !== id);
      setState({ ...state, roteiros: updated });
      setCurrentRoteiro(updated[0] || null);
      toast.success("Excluído");
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
  };

  const addBigIdea = async (payload) => {
    try {
      const { data } = await api.post(`/content/${activeBrand.brand_id}/bigidea`, payload);
      setState((s) => ({ ...s, big_ideas: [...s.big_ideas, data] }));
      toast.success("Big Idea salva");
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
  };

  const delBigIdea = async (id) => {
    try {
      await api.delete(`/content/${activeBrand.brand_id}/bigidea/${id}`);
      setState((s) => ({ ...s, big_ideas: s.big_ideas.filter((b) => b.id !== id) }));
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
  };

  const useBigIdea = (teseNova) => {
    if (!currentRoteiro) { toast.error("Crie um roteiro primeiro"); return; }
    setField("passo_3", "big_idea", teseNova);
    toast.success("Big Idea aplicada no passo 3");
  };

  const askAI = async (step) => {
    if (!currentRoteiro) return;
    if (!step.aiPrompt) return;
    setAiLoading((l) => ({ ...l, [step.key]: true }));
    try {
      const data = (currentRoteiro.passos || {})[step.key] || {};
      const bigIdea = (currentRoteiro.passos?.passo_3 || {}).big_idea;
      const prompt = step.aiPrompt(data, currentRoteiro.tema, bigIdea);
      const r = await api.post("/ai/assist", { prompt, max_tokens: 700 });
      setAiModal({ title: step.title, text: r.data.text });
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    finally { setAiLoading((l) => ({ ...l, [step.key]: false })); }
  };

  const copyAIToClipboard = async () => {
    if (!aiModal) return;
    try {
      await navigator.clipboard.writeText(aiModal.text);
      toast.success("Copiado");
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  return (
    <div className="p-6 lg:p-10" data-testid="content-page">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest grad-text font-bold mb-1">// módulo · conteúdo</div>
          <h1 className="text-3xl font-extrabold">Conteúdo & Escrita Persuasiva</h1>
          <p className="text-muted text-sm mt-1">Roteiro em 6 passos, com Big Ideas reutilizáveis e IA por passo.</p>
        </div>
        <div className="flex items-center gap-3">
          {saving && <span className="text-xs text-muted flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> salvando…</span>}
          <button onClick={createRoteiro} className="btn-grad text-sm flex items-center gap-1" data-testid="new-roteiro"><Plus size={14} /> Novo roteiro</button>
        </div>
      </div>

      {/* Roteiro tabs */}
      {state.roteiros.length > 0 && (
        <div className="flex gap-2 mb-4 flex-wrap" data-testid="roteiro-tabs">
          {state.roteiros.map((r) => (
            <div key={r.id} className={`card-2 px-3 py-1.5 flex items-center gap-2 cursor-pointer ${currentRoteiro?.id === r.id ? "ring-2 ring-[#C05BFF]" : ""}`} onClick={() => setCurrentRoteiro(r)} data-testid={`roteiro-tab-${r.id}`}>
              <FileText size={12} className="text-muted" />
              <span className="text-sm truncate max-w-[180px]">{r.title || "Sem título"}</span>
              <button onClick={(e) => { e.stopPropagation(); deleteRoteiro(r.id); }} className="text-muted hover:text-[var(--error)]"><X size={12} /></button>
            </div>
          ))}
        </div>
      )}

      {!currentRoteiro ? (
        <div className="card p-10 text-center">
          <FileText size={40} className="mx-auto text-muted mb-3" />
          <h3 className="text-lg font-bold mb-1">Nenhum roteiro ainda</h3>
          <p className="text-sm text-muted mb-4">Comece criando o primeiro roteiro persuasivo desta marca.</p>
          <button onClick={createRoteiro} className="btn-grad inline-flex items-center gap-1 text-sm"><Plus size={14} /> Criar roteiro</button>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_340px] gap-6">
          <div className="space-y-4">
            {/* Header: title + Tema / Abordagem / Formato */}
            <div className="card p-5 space-y-3">
              <input className="input text-lg font-bold" value={currentRoteiro.title || ""} onChange={(e) => setHeader({ title: e.target.value })} placeholder="Título do roteiro" data-testid="roteiro-title" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-muted font-mono">Tema</label>
                  <input className="input mt-1" value={currentRoteiro.tema || ""} onChange={(e) => setHeader({ tema: e.target.value })} placeholder="Ex: Black Friday, Rebranding…" data-testid="roteiro-tema" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-muted font-mono">Abordagem</label>
                  <input className="input mt-1" value={currentRoteiro.abordagem || ""} onChange={(e) => setHeader({ abordagem: e.target.value })} placeholder="Ex: Autoridade, História…" data-testid="roteiro-abordagem" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-muted font-mono">Formato</label>
                  <select className="input mt-1" value={currentRoteiro.formato || ""} onChange={(e) => setHeader({ formato: e.target.value })} data-testid="roteiro-formato">
                    <option value="">Selecione…</option>
                    {FORMATOS.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* 6 steps */}
            {STEPS.map((step) => {
              const stepData = (currentRoteiro.passos || {})[step.key] || {};
              return (
                <div key={step.key} className="card p-5 space-y-3" data-testid={`step-${step.key}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-muted">passo {step.key.split("_")[1]} de 6</div>
                      <h3 className="text-lg font-bold">{step.title}</h3>
                      <p className="text-xs text-muted mt-0.5">{step.desc}</p>
                    </div>
                    <button onClick={() => askAI(step)} disabled={aiLoading[step.key]} className="btn-outline text-xs flex items-center gap-1 flex-shrink-0" data-testid={`ai-${step.key}`}>
                      {aiLoading[step.key] ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
                      IA
                    </button>
                  </div>
                  <div className="space-y-3">
                    {step.fields.map((f) => (
                      <div key={f.key}>
                        <label className="text-[10px] uppercase tracking-widest text-muted font-mono">{f.label}</label>
                        <div className="mt-1"><Field f={f} value={stepData[f.key]} onChange={(v) => setField(step.key, f.key, v)} /></div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Sidebar */}
          <aside className="space-y-4">
            <ChecklistCard passos={currentRoteiro.passos || {}} />
            <AssembledCard passos={currentRoteiro.passos || {}} tema={currentRoteiro.tema} formato={currentRoteiro.formato} />
            <BigIdeasBank items={state.big_ideas} onAdd={addBigIdea} onDelete={delBigIdea} onUse={useBigIdea} />
          </aside>
        </div>
      )}

      {aiModal && (
        <div className="tour-overlay" onClick={() => setAiModal(null)} data-testid="ai-modal">
          <div className="card" style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", maxWidth: 640, width: "calc(100% - 32px)", padding: 24, zIndex: 61 }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3 gap-3">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest grad-text font-bold">// sugestão da ia</div>
                <h3 className="text-lg font-bold">{aiModal.title}</h3>
              </div>
              <button onClick={() => setAiModal(null)} className="text-muted hover:text-[var(--txt)]" data-testid="ai-modal-close"><X size={18} /></button>
            </div>
            <div className="card-2 p-4 text-sm leading-relaxed whitespace-pre-wrap max-h-[60vh] overflow-y-auto">{aiModal.text}</div>
            <div className="flex gap-2 mt-3">
              <button onClick={copyAIToClipboard} className="btn-grad text-sm flex items-center gap-1" data-testid="ai-modal-copy"><Copy size={12} /> Copiar</button>
              <button onClick={() => setAiModal(null)} className="btn-outline text-sm">Fechar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
